// ---------------------------------------------------------------------------
// THE CLIENT PORTAL'S DECISIONS, AS PURE FUNCTIONS. V24.
//
// No React, no DOM, no colour — so every rule below is assertable under
// `node --test`, and so scripts/lib/portal-render.mjs can render the screen that
// spends them without a browser.
//
// THE TWO RULES HERE THAT 18-client-portal.md §8 CHECKS BY NAME:
//
//   check 11  stale-data honesty. `freshness` decides it, and the screen renders
//             what it returns. §7.3: "It never shows a stale number as if it
//             were current. On an ops-room wall a confidently wrong number is
//             worse than a blank screen."
//   check 8   the scope-reduction annotation. `trendBars` carries it through to
//             the bar it belongs to rather than dropping it, because a trend
//             renderer that only needed a number would drop it silently and
//             §V17.4's whole point is that the annotation reaches THIS surface.
// ---------------------------------------------------------------------------

import { translate, type Locale } from "../i18n/translate";
import { readerIntlTag } from "./format";
import type { TrendPoint } from "./wire-types";
import type { Tone } from "../design/tone";

/**
 * §7.1's two poll rates.
 *
 * "Poll every 15 seconds outside the operational window and every 5 seconds
 * inside it, matching V20's own five-second target."
 *
 * A FUNCTION AND NOT A CONSTANT ON THE SCREEN, because the screen learns whether
 * the window is open from the same response it is polling — so the interval has
 * to be recomputed from data on every tick, and a value captured once at mount
 * would poll a live event at the resting rate for the whole day.
 */
export const POLL_INSIDE_WINDOW_MS = 5_000;
export const POLL_OUTSIDE_WINDOW_MS = 15_000;

export function pollIntervalMs(inWindow: boolean): number {
  return inWindow ? POLL_INSIDE_WINDOW_MS : POLL_OUTSIDE_WINDOW_MS;
}

/**
 * How many intervals may pass before the number on screen stops being current.
 *
 * TWO, NOT ONE. One means every poll that arrives a few hundred milliseconds
 * late paints a stale marker, and a marker that appears constantly is a marker
 * nobody reads — the same disease as a permanently red tile. Two means one whole
 * missed poll plus slack, which on a live event is ten seconds.
 */
export const STALE_AFTER_INTERVALS = 2;

export type Freshness = {
  /** True when the number beside this must not be read as current. */
  stale: boolean;
  /** The word. Carries the meaning with the colour removed, per §7.5. */
  label: string;
  tone: Tone;
  /** Milliseconds since the last successful answer, or null if there never was one. */
  ageMs: number | null;
};

/**
 * Is what is on screen current?
 *
 * `lastOkAt` is WHEN THE ANSWER ARRIVED HERE, not when the server computed the
 * score. Those are different questions and only one of them is check 11's: a
 * severed poll leaves `computed_at` frozen at a perfectly plausible timestamp,
 * so a screen that judged freshness from the payload would go on reporting the
 * data as fresh forever. It is the round trip that stopped, and the round trip
 * is what has to be measured.
 */
export function freshness(args: {
  lastOkAt: number | null;
  now: number;
  intervalMs: number;
}): Freshness {
  const { lastOkAt, now, intervalMs } = args;
  if (lastOkAt === null) {
    return { stale: true, label: "Not updating", tone: "attention", ageMs: null };
  }
  const ageMs = Math.max(0, now - lastOkAt);
  const stale = ageMs > intervalMs * STALE_AFTER_INTERVALS;
  return {
    stale,
    label: stale ? "Not updating" : "Updating live",
    tone: stale ? "attention" : "live",
    ageMs,
  };
}

/**
 * The clock, to the second.
 *
 * system/format.ts's `clock` stops at minutes, which is right for a shift start
 * and wrong here: §7.3's example is `last updated 14:31:02`, and on a wall the
 * seconds are the only part that tells a watcher whether the thing is moving.
 */
export function secondsClock(iso: string | null): string {
  if (iso === null) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleTimeString(readerIntlTag(), { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

export type TrendBar = {
  at: string;
  score: number | null;
  /** 0–100. The BAR. */
  heightPercent: number;
  /** The NUMBER, beside the bar, because §7.5 forbids a fill alone. */
  scoreLabel: string;
  /** §V17.4. Rendered, never summarised away. */
  annotation: string | null;
};

/**
 * The trend series, ready to draw.
 *
 * A NULL SCORE IS A ZERO-HEIGHT BAR AND NOT A MISSING ONE. A sample whose score
 * could not be computed still happened, and dropping it would close the gap in
 * the series — which is the visual claim that nothing occurred at that time. It
 * renders as a bar with no height and a dash, which is a different claim and the
 * true one.
 */
export function trendBars(trend: readonly TrendPoint[]): TrendBar[] {
  return trend.map((point) => ({
    at: point.at,
    score: point.score,
    heightPercent: point.score === null ? 0 : Math.max(0, Math.min(100, Math.round(point.score))),
    scoreLabel: point.score === null ? "—" : String(Math.round(point.score)),
    annotation: point.annotation,
  }));
}

/**
 * §4's S1 zone 1, second line: `142 of 148 posts covered · 2 critical posts dark`.
 *
 * Assembled from the SAME counts the score is, which is why the API sends both
 * halves rather than letting a screen derive one. And the critical count is never
 * folded into the percentage on screen even though it is inside the maths —
 * §4's own instruction, because "a client reads a big green number and stops".
 */
export function coverageSentence(
  coverage: { seats: number; covered: number },
  darkCritical: number,
  locale: Locale = "en",
): string {
  // S145. Through the catalogue, so the portal reads it in the client's language.
  const posts = translate(locale, "{covered} of {seats} posts covered", { covered: coverage.covered, seats: coverage.seats });
  if (darkCritical === 0) return posts;
  const dark =
    darkCritical === 1
      ? translate(locale, "{count} critical post dark", { count: darkCritical })
      : translate(locale, "{count} critical posts dark", { count: darkCritical });
  return `${posts} · ${dark}`;
}

/**
 * The tone for a seat state on the roster.
 *
 * IT KEYS ON THE DERIVATION, NEVER ON THE CLIENT-FACING WORD, and the difference
 * is the whole reason this function has the shape it does.
 *
 * `app.portal_seat_state.derived_from` says what ESTABLISHES a state — a plan, a
 * vacancy, a standby arrangement, an arrival, a departure — and it is a CHECK
 * constraint vocabulary about mechanism. The ids and labels beside it are the
 * words a client reads, and HANDOFF.md §6 records three consecutive slices in
 * which one of those words, typed into TypeScript, collided with an assignment
 * status. `vacant` is not `unfilled` and `reopened` is not `released` precisely
 * because somebody wanted to key on a word in this layer. So this keys on the
 * mechanism instead, which no client-facing rename can move.
 *
 * verify-V24.sh asserts these five names against the CHECK constraint itself, so
 * a sixth derivation added in SQL fails the suite rather than falling through to
 * neutral in a browser nobody is watching.
 *
 * AN UNKNOWN DERIVATION IS NEUTRAL, which is the safe direction: a state this
 * function has not been taught renders as its word with a plain dot, never as a
 * confident green.
 */
export const SEAT_DERIVATION_TONE: Record<string, Tone> = {
  vacancy: "failed",
  standby: "attention",
  arrival: "live",
  departure: "live",
  plan: "neutral",
};

export function seatTone(derivedFrom: string): Tone {
  return SEAT_DERIVATION_TONE[derivedFrom] ?? "neutral";
}
